import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { availabilityService } from '../services/availabilityService'
import { 
  AvailabilityProfile, 
  SchedulingPreferences, 
  TimeSlot 
} from '../types/availability'
import {
  ClockIcon,
  CalendarDaysIcon,
  CogIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  BoltIcon
} from '@heroicons/react/24/outline'

interface AvailabilityManagerProps {
  isOpen: boolean
  onClose: () => void
}

const AvailabilityManager = ({ isOpen, onClose }: AvailabilityManagerProps) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'schedule' | 'preferences' | 'smart'>('schedule')
  
  const [profile, setProfile] = useState<AvailabilityProfile | null>(null)
  const [preferences, setPreferences] = useState<SchedulingPreferences | null>(null)
  const [editingDay, setEditingDay] = useState<number | null>(null)
  const [newTimeSlot, setNewTimeSlot] = useState({ start: '09:00', end: '17:00' })

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  useEffect(() => {
    if (isOpen && user?.id) {
      loadData()
    }
  }, [isOpen, user?.id])

  const loadData = async () => {
    try {
      setLoading(true)
      const [profiles, prefs] = await Promise.all([
        availabilityService.getProfiles(user!.id),
        availabilityService.getSchedulingPreferences(user!.id)
      ])

      const defaultProfile = profiles.find(p => p.isDefault) || profiles[0]
      setProfile(defaultProfile)
      setPreferences(prefs)
    } catch (error) {
      console.error('Error loading availability data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDayToggle = (dayIndex: number) => {
    if (!profile) return

    const updatedWorkingDays = profile.workingDays.map(day => 
      day.dayOfWeek === dayIndex 
        ? { ...day, enabled: !day.enabled }
        : day
    )

    setProfile({ ...profile, workingDays: updatedWorkingDays })
  }

  const handleAddTimeSlot = (dayIndex: number) => {
    if (!profile) return

    const newSlot: TimeSlot = {
      id: `slot-${Date.now()}`,
      start: newTimeSlot.start,
      end: newTimeSlot.end,
      available: true
    }

    const updatedWorkingDays = profile.workingDays.map(day =>
      day.dayOfWeek === dayIndex
        ? { ...day, timeSlots: [...day.timeSlots, newSlot] }
        : day
    )

    setProfile({ ...profile, workingDays: updatedWorkingDays })
    setEditingDay(null)
    setNewTimeSlot({ start: '09:00', end: '17:00' })
  }

  const handleRemoveTimeSlot = (dayIndex: number, slotId: string) => {
    if (!profile) return

    const updatedWorkingDays = profile.workingDays.map(day =>
      day.dayOfWeek === dayIndex
        ? { ...day, timeSlots: day.timeSlots.filter(slot => slot.id !== slotId) }
        : day
    )

    setProfile({ ...profile, workingDays: updatedWorkingDays })
  }

  const handlePreferenceChange = (key: keyof SchedulingPreferences, value: any) => {
    if (!preferences) return
    setPreferences({ ...preferences, [key]: value })
  }

  const handleSave = async () => {
    if (!profile || !preferences) return

    try {
      setSaving(true)
      await Promise.all([
        availabilityService.updateProfile(profile.id, profile),
        availabilityService.updatePreferences(user!.id, preferences)
      ])
      onClose()
    } catch (error) {
      console.error('Error saving availability settings:', error)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />
        <div className="relative glass-mockup rounded-2xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Loading availability settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-6xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
              <CalendarDaysIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Availability Management</h2>
              <p className="text-gray-400 text-sm">Configure your working hours and scheduling preferences</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {[
            { id: 'schedule', name: 'Working Hours', icon: ClockIcon },
            { id: 'preferences', name: 'Preferences', icon: CogIcon },
            { id: 'smart', name: 'Smart Features', icon: SparklesIcon }
          ].map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            )
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <AnimatePresence mode="wait">
            {/* Working Hours Tab */}
            {activeTab === 'schedule' && (
              <motion.div
                key="schedule"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Buffer Times */}
                  <div className="glass-mockup rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Buffer Times</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Buffer Before (minutes)
                        </label>
                        <input
                          type="number"
                          value={profile?.bufferBefore || 0}
                          onChange={(e) => setProfile(prev => prev ? { ...prev, bufferBefore: parseInt(e.target.value) || 0 } : null)}
                          className="input-field"
                          min="0"
                          max="60"
                        />
                      </div>
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Buffer After (minutes)
                        </label>
                        <input
                          type="number"
                          value={profile?.bufferAfter || 0}
                          onChange={(e) => setProfile(prev => prev ? { ...prev, bufferAfter: parseInt(e.target.value) || 0 } : null)}
                          className="input-field"
                          min="0"
                          max="60"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Travel Time */}
                  <div className="glass-mockup rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Travel Settings</h3>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Default Travel Time (minutes)
                      </label>
                      <input
                        type="number"
                        value={profile?.travelTime || 0}
                        onChange={(e) => setProfile(prev => prev ? { ...prev, travelTime: parseInt(e.target.value) || 0 } : null)}
                        className="input-field"
                        min="0"
                        max="120"
                      />
                    </div>
                  </div>
                </div>

                {/* Weekly Schedule */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Weekly Schedule</h3>
                  
                  {dayNames.map((dayName, dayIndex) => {
                    const dayData = profile?.workingDays.find(d => d.dayOfWeek === dayIndex)
                    
                    return (
                      <div key={dayIndex} className="glass-mockup rounded-xl p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleDayToggle(dayIndex)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                dayData?.enabled
                                  ? 'bg-blue-500 border-blue-500 text-white'
                                  : 'border-gray-400 text-transparent'
                              }`}
                            >
                              <CheckIcon className="w-3 h-3" />
                            </button>
                            <h4 className="text-white font-medium">{dayName}</h4>
                          </div>
                          
                          {dayData?.enabled && (
                            <button
                              onClick={() => setEditingDay(editingDay === dayIndex ? null : dayIndex)}
                              className="p-2 text-blue-400 hover:text-blue-300 transition-colors rounded-lg hover:bg-white/10"
                            >
                              <PlusIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {dayData?.enabled && (
                          <div className="space-y-3">
                            {/* Existing Time Slots */}
                            {dayData.timeSlots.map((slot) => (
                              <div key={slot.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <ClockIcon className="w-4 h-4 text-gray-400" />
                                  <span className="text-white font-medium">
                                    {slot.start} - {slot.end}
                                  </span>
                                  {!slot.available && (
                                    <span className="text-red-400 text-sm">({slot.reason})</span>
                                  )}
                                </div>
                                
                                <button
                                  onClick={() => handleRemoveTimeSlot(dayIndex, slot.id)}
                                  className="p-1 text-red-400 hover:text-red-300 transition-colors"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            ))}

                            {/* Add New Time Slot */}
                            {editingDay === dayIndex && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="flex items-center space-x-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg"
                              >
                                <input
                                  type="time"
                                  value={newTimeSlot.start}
                                  onChange={(e) => setNewTimeSlot(prev => ({ ...prev, start: e.target.value }))}
                                  className="input-field text-sm"
                                />
                                <span className="text-gray-400">to</span>
                                <input
                                  type="time"
                                  value={newTimeSlot.end}
                                  onChange={(e) => setNewTimeSlot(prev => ({ ...prev, end: e.target.value }))}
                                  className="input-field text-sm"
                                />
                                <button
                                  onClick={() => handleAddTimeSlot(dayIndex)}
                                  className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                  <CheckIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingDay(null)}
                                  className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </motion.div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </motion.div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && preferences && (
              <motion.div
                key="preferences"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Booking Rules */}
                  <div className="glass-mockup rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Booking Rules</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Default Duration (minutes)
                        </label>
                        <input
                          type="number"
                          value={preferences.defaultDuration}
                          onChange={(e) => handlePreferenceChange('defaultDuration', parseInt(e.target.value) || 60)}
                          className="input-field"
                          min="15"
                          step="15"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Max Daily Bookings
                        </label>
                        <input
                          type="number"
                          value={preferences.maxDailyBookings}
                          onChange={(e) => handlePreferenceChange('maxDailyBookings', parseInt(e.target.value) || 1)}
                          className="input-field"
                          min="1"
                          max="10"
                        />
                      </div>

                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Minimum Booking Notice (hours)
                        </label>
                        <input
                          type="number"
                          value={profile?.minBookingNotice || 24}
                          onChange={(e) => setProfile(prev => prev ? { ...prev, minBookingNotice: parseInt(e.target.value) || 24 } : null)}
                          className="input-field"
                          min="1"
                          max="168"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Scheduling Preferences */}
                  <div className="glass-mockup rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Scheduling Preferences</h3>
                    <div className="space-y-4">
                      {[
                        { key: 'allowBackToBack', label: 'Allow Back-to-Back Bookings' },
                        { key: 'requireBuffer', label: 'Require Buffer Time' },
                        { key: 'autoDeclineConflicts', label: 'Auto-Decline Conflicts' },
                        { key: 'workingHoursOnly', label: 'Working Hours Only' },
                        { key: 'weekendBookings', label: 'Allow Weekend Bookings' },
                        { key: 'holidayBookings', label: 'Allow Holiday Bookings' }
                      ].map((setting) => (
                        <label key={setting.key} className="flex items-center space-x-3 cursor-pointer">
                          <button
                            onClick={() => handlePreferenceChange(setting.key as keyof SchedulingPreferences, !preferences[setting.key as keyof SchedulingPreferences])}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              preferences[setting.key as keyof SchedulingPreferences]
                                ? 'bg-blue-500 border-blue-500 text-white'
                                : 'border-gray-400 text-transparent'
                            }`}
                          >
                            <CheckIcon className="w-3 h-3" />
                          </button>
                          <span className="text-white text-sm">{setting.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Preferred Start Times */}
                <div className="glass-mockup rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Preferred Start Times</h3>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {Array.from({ length: 12 }, (_, i) => {
                      const hour = i + 8 // 8 AM to 7 PM
                      const timeString = `${hour.toString().padStart(2, '0')}:00`
                      const isSelected = preferences.preferredStartTimes.includes(timeString)
                      
                      return (
                        <button
                          key={timeString}
                          onClick={() => {
                            const newTimes = isSelected
                              ? preferences.preferredStartTimes.filter(t => t !== timeString)
                              : [...preferences.preferredStartTimes, timeString]
                            handlePreferenceChange('preferredStartTimes', newTimes)
                          }}
                          className={`p-3 rounded-lg font-medium transition-colors ${
                            isSelected
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                          }`}
                        >
                          {hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Smart Features Tab */}
            {activeTab === 'smart' && (
              <motion.div
                key="smart"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* AI Scheduling */}
                <div className="glass-mockup rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <SparklesIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">AI-Powered Scheduling</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Smart Suggestions</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Get AI-powered time slot recommendations based on your preferences, energy levels, and client history.
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-green-400">
                        <CheckIcon className="w-4 h-4" />
                        <span>Enabled</span>
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-r from-blue-500/10 to-green-500/10 border border-blue-500/30 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Workload Optimization</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Automatically balance your daily schedule to prevent burnout and optimize productivity.
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-green-400">
                        <CheckIcon className="w-4 h-4" />
                        <span>Enabled</span>
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Travel Time Optimization</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Smart scheduling to minimize travel time between locations and maximize efficiency.
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-orange-400">
                        <BoltIcon className="w-4 h-4" />
                        <span>Coming Soon</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calendar Integration */}
                <div className="glass-mockup rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                      <CalendarDaysIcon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Calendar Integration</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Google Calendar</h4>
                        <span className="text-orange-400 text-sm">Coming Soon</span>
                      </div>
                      <p className="text-gray-300 text-sm">
                        Two-way sync with Google Calendar to prevent double bookings and keep all your events in one place.
                      </p>
                    </div>

                    <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">Outlook Calendar</h4>
                        <span className="text-orange-400 text-sm">Coming Soon</span>
                      </div>
                      <p className="text-gray-300 text-sm">
                        Seamless integration with Microsoft Outlook for professional calendar management.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-sm text-gray-400">
            Changes will be applied to your default availability profile
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default AvailabilityManager 